import {
  Box,
  Button,
  ColumnLayout,
  FormField,
  Input,
  InputProps,
  NonCancelableCustomEvent,
} from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { publicationTitleInputMaxLength, validProtocol } from '../../api/UtilsApi';
import constants from '../../common/constants';
import { OtherPublicationItem } from './APGLUserProfileUtils';
import { MoveVerticallyHandle } from '../common/MoveVerticallyHandle';

interface Props {
  item: OtherPublicationItem;
  handlePublicationChanged: (pub: OtherPublicationItem) => void;
  handlePublicationDeleted: (id: number) => void;
  handleInputErrorUpdated: (id: number, titleError: string, urlError: string) => void; // input validation
  titleAlert?: string;
  urlAlert?: string;
}

const OtherPublicationInput: React.FunctionComponent<Props> = (props: Props) => {
  const {
    item,
    titleAlert,
    urlAlert,
    handlePublicationChanged,
    handlePublicationDeleted,
    handleInputErrorUpdated,
  } = props;
  const { title, url, id } = item;

  const [pubTitle, setPubTitle] = useState<string>(title);
  const [pubUrl, setPubUrl] = useState<string>(url);
  const [titleError, setTitleError] = useState<string>('');
  const [urlError, setUrlError] = useState<string>('');

  useEffect(() => {
    handlePublicationChanged({ title: pubTitle, url: pubUrl, id });
    if (!pubTitle && !pubUrl) {
      setTitleError('');
      setUrlError('');
    }
  }, [pubTitle, pubUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleInputErrorUpdated(id, titleError, urlError);
  }, [titleError, urlError]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateTitle = (name: string) => {
    if (!name && pubUrl) {
      setTitleError(constants.PUBLICATION_TITLE_EMPTY_ERROR);
    } else if (name.length > publicationTitleInputMaxLength) {
      setTitleError(`Must be less than ${publicationTitleInputMaxLength} characters.`);
    } else {
      setTitleError('');
    }
  };

  const onTitleChanged = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    const newTitle = event.detail.value;
    setPubTitle(newTitle);
    validateTitle(newTitle);
  };

  const onTitleBlur = () => {
    if (pubTitle && !pubUrl) {
      setUrlError(constants.PUBLICATION_URL_EMPTY_ERROR);
    }
  };

  const validateUrl = (link: string) => {
    if (!link && pubTitle) {
      setUrlError(constants.PUBLICATION_URL_EMPTY_ERROR);
    } else if (link && !link.startsWith(validProtocol)) {
      setUrlError(constants.PUBLICATION_URL_HTTPS_ERROR);
    } else if (link.includes(' ')) {
      setUrlError(constants.PUBLICATION_URL_NO_SPACE_ERROR);
    } else if (!link.includes('.')) {
      setUrlError(constants.PUBLICATION_URL_INVALID_ERROR);
    } else {
      setUrlError('');
    }
  };

  const onUrlChanged = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    const newUrl = event.detail.value;
    setPubUrl(newUrl);
    validateUrl(newUrl);
  };

  const onUrlBlur = () => {
    if (!pubTitle && pubUrl) {
      setTitleError(constants.PUBLICATION_TITLE_EMPTY_ERROR);
    }
  };

  const onDeletePublication = () => {
    handleInputErrorUpdated(id, '', ''); // clear errors
    handlePublicationDeleted(id);
  };

  return (
    <ColumnLayout columns={3}>
      <FormField
        errorText={titleError || titleAlert}
        stretch
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <Input
          value={pubTitle}
          onChange={onTitleChanged}
          onBlur={onTitleBlur}
          placeholder={'Enter publication title'}
          ariaRequired
        />
      </FormField>
      <FormField
        errorText={urlError || urlAlert}
        stretch
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <Input
          value={pubUrl}
          onChange={onUrlChanged}
          onBlur={onUrlBlur}
          placeholder={'Enter URL'}
          ariaRequired
        />
      </FormField>
      <Box>
        <div id="delete-other-publication-icon">
          <Button
            className="cursor-pointer"
            variant="icon"
            iconUrl={'/images/icon-delete.svg'}
            iconAlt="Delete icon"
            ariaLabel="Delete publication"
            onClick={onDeletePublication}
          />
        </div>
        <div id="sort-other-publication-icon">
          <MoveVerticallyHandle />
        </div>
      </Box>
    </ColumnLayout>
  );
};

export default OtherPublicationInput;
